import { Play, User } from 'lucide-react';
import { Game } from '../../types/game';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAnalytics } from '../../hooks/useAnalytics';
import CategoryTag from './CategoryTag';
import { formatCurrency } from '../../utils/currency';

interface GameCardProps {
  game: Game;
}

// Default placeholder - clean light gray with centered Lucide Gamepad2 icon
const DEFAULT_GAME_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23f1f5f9"/%3E%3Cg transform="translate(200,150) scale(4.7) translate(-12,-12)"%3E%3Cline x1="6" y1="11" x2="10" y2="11" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cline x1="8" y1="9" x2="8" y2="13" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cline x1="15" y1="12" x2="15.01" y2="12" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cline x1="18" y1="10" x2="18.01" y2="10" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cpath d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z" fill="none" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3C/g%3E%3C/svg%3E';

export default function GameCard({ game }: GameCardProps) {
  const { trackGameAction } = useAnalytics();
  const navigate = useNavigate();
  
  const handleCardClick = () => {
    trackGameAction('game_card_clicked', game.id.toString(), {
      game_name: game.name,
      category: game.tags?.[0] || 'unknown'
    });
  };
  
  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    trackGameAction('play_button_clicked', game.id.toString(), {
      game_name: game.name,
      source: 'game_card_quick_play'
    });
    
    // Check if game is paid and user doesn't own it
    const needsToPurchase = game.pricing?.is_paid && !game.user_owns;
    
    if (needsToPurchase) {
      // Navigate to game details page to purchase
      navigate(`/games/${game.id}`);
      return;
    }
    
    // Calculate popup dimensions for portrait mode (9:16 aspect ratio)
    const width = 420;
    const height = Math.round(width * 16 / 9);  // Portrait: 420 * 16/9 = 747px
    
    // Center the popup on the screen
    const left = Math.round((window.screen.width - width) / 2);
    const top = Math.round((window.screen.height - height) / 2);
    
    // Open popup window with game in portrait mode
    const popup = window.open(
      `/play/${game.id}`,
      'beemi-game-player',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=yes`
    );
    
    if (!popup) {
      toast.error('Popup blocked! Please allow popups for this site.');
    }
  };

  // Get the best available image
  const imageUrl = game.icon_url || game.cover_image_url || DEFAULT_GAME_IMAGE;
  
  // Get author display name
  const authorName = game.author?.name || game.team_name || 'Unknown';

  return (
    <Link to={`/games/${game.id}`} onClick={handleCardClick}>
      <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl overflow-hidden h-full group hover:shadow-lg hover:-translate-y-1 transition-all duration-200">
        {/* Thumbnail */}
        <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800">
          <img
            src={imageUrl}
            alt={game.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            onError={(e) => {
              // Fallback to default image on error
              (e.target as HTMLImageElement).src = DEFAULT_GAME_IMAGE;
            }}
          />
          
          {/* Price Badge (if paid game) */}
          {game.pricing?.is_paid && (
            <div className="absolute top-2 left-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-2.5 py-1 rounded-lg text-sm font-bold shadow-lg backdrop-blur-sm border border-white/20">
              {formatCurrency(game.pricing.price, game.pricing.currency || 'USD')}
            </div>
          )}
          
          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none" />
          
          {/* Quick Play Button */}
          <button
            onClick={handlePlayClick}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white rounded-full p-4 transform hover:scale-110 active:scale-95 transition-all duration-200 opacity-0 group-hover:opacity-100 z-10"
            style={{ background: 'linear-gradient(90deg, #FCB902 0%, #FF00CF 100%)' }}
            title="Play Now"
          >
            <Play className="w-8 h-8 fill-current" />
          </button>
          
          {/* Stats Overlay */}
          <div className="absolute bottom-2 right-2 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm rounded-lg px-2.5 py-1.5">
            <Play className="w-4 h-4 text-white fill-white" />
            <span className="text-sm font-bold text-white">
              {game.total_plays >= 1000 ? `${(game.total_plays / 1000).toFixed(1)}K` : game.total_plays || 0}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-2">
          <h3 className="font-semibold text-base mb-2 line-clamp-2 text-gray-900 dark:text-dark-text">
            {game.name}
          </h3>
          
          {/* Tags */}
          {game.tags && game.tags.length > 0 && (
            <div className="flex gap-1.5 flex-wrap mb-2">
              {game.tags.slice(0, 3).map((tag, index) => (
                <CategoryTag key={index} categoryId={tag} size="sm" />
              ))}
            </div>
          )}
          
          {/* Author */}
          {game.author && (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-gray-400 dark:text-dark-text-muted">by</span>
              {game.author.image ? (
                <img
                  src={game.author.image}
                  alt={game.author.name}
                  className="w-4 h-4 rounded-full object-cover"
                />
              ) : (
                <div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-dark-surface-secondary flex items-center justify-center">
                  <User className="w-2.5 h-2.5 text-gray-400 dark:text-dark-text-muted" />
                </div>
              )}
              <span className="text-sm font-medium text-gray-700 dark:text-dark-text">
                {authorName}
              </span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
